import { api } from '@/lib/api/client';
import { uploadFileApi } from '@api-client/files';
import { type FederalClassification, type Gender, UserPlan } from '@shared/enums/domain.enum';
import type { Role } from '@shared/enums/role.enum';
import type {
  AdminStatsResponse,
  AuthUser,
  GdprAnonymizeResult,
  GdprExportData,
  PlayerHistoryResponse,
  PlayerStatsResponse,
} from '@shared/types/entities';

import { type PaginatedResponse, buildQuery } from './types';

export interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  role?: Role;
  disabled?: boolean;
  clubId?: number;
  includeDeleted?: boolean;
}

export interface UpdateUserPayload {
  role?: Role;
  disabled?: boolean;
  firstName?: string;
  lastName?: string;
  username?: string;
  mobile?: string;
  licenseNumber?: string;
  userPlan?: UserPlan;
  birthDate?: string;
  gender?: Gender;
  federalClassification?: FederalClassification;
}

export async function getUsersApi(params?: GetUsersParams): Promise<PaginatedResponse<AuthUser>> {
  return api.get(`/users${buildQuery(params as Record<string, unknown>)}`);
}

export async function getUserApi(id: number): Promise<AuthUser> {
  return api.get(`/users/${id}`);
}

export async function updateUserApi(id: number, data: UpdateUserPayload): Promise<AuthUser> {
  return api.patch(`/users/${id}`, data);
}

export interface UpdateMePayload {
  firstName?: string;
  lastName?: string;
  username?: string;
  mobile?: string;
  licenseNumber?: string;
  birthDate?: string;
  gender?: string;
  isProfilePublic?: boolean;
}

export async function updateMeApi(data: UpdateMePayload): Promise<AuthUser> {
  return api.patch('/users/me', data);
}

// ─── Player History ────────────────────────────────────────────

export interface GetPlayerHistoryParams {
  page?: number;
  limit?: number;
  year?: number;
}

export async function getMyHistoryApi(params?: GetPlayerHistoryParams): Promise<PlayerHistoryResponse> {
  return api.get(`/users/me/history${buildQuery(params as Record<string, unknown>)}`);
}

export async function getMyStatsApi(): Promise<PlayerStatsResponse> {
  return api.get('/users/me/stats');
}

export async function getAdminStatsApi(): Promise<AdminStatsResponse> {
  return api.get('/users/admin/stats');
}

export async function uploadUserAvatarApi(
  userId: number,
  file: File,
): Promise<{ id: number; key: string; mimeType: string }> {
  return uploadFileApi({ usage: 'USER_AVATAR', file, extraFields: { userId: String(userId) } });
}

// ─── Stripe Subscription (Joueur Plus) ────────────────────────

export async function userStripeSetupIntentApi(): Promise<{ clientSecret: string }> {
  return api.post('/users/me/stripe/setup-intent', {});
}

export async function userStripeSubscribeApi(
  interval: 'monthly' | 'annual',
  paymentMethodId?: string,
): Promise<{ subscriptionId: string; status: string; clientSecret?: string }> {
  return api.post('/users/me/stripe/subscribe', { interval, ...(paymentMethodId && { paymentMethodId }) });
}

export async function userStripeBillingPortalApi(): Promise<{ portalUrl: string }> {
  return api.post('/users/me/stripe/billing-portal', {});
}

// ─── RGPD ─────────────────────────────────────────────────────

export async function exportMyGdprDataApi(): Promise<GdprExportData> {
  return api.get('/users/me/gdpr/export');
}

export async function anonymizeMyAccountApi(): Promise<GdprAnonymizeResult> {
  return api.post('/users/me/gdpr/anonymize', { confirm: true });
}
